import React, { useState, useEffect, useMemo } from 'react';
import CentralCalls from '../../centralCalls';
import { TextField, FormControl, IconButton } from '@mui/material';
import WipLink from '../WipLink';
import { makeStyles } from '@mui/styles';
import { useNavigate } from 'react-router-dom';
import PhaseLink from '../PhaseLink';
import Autocomplete from '@mui/material/Autocomplete';
import JSONTableEditor from '../JSONTableEditor';
import { setHours } from 'date-fns';
import { faPlus, faPrint, faDollarSign } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { parseISO, differenceInCalendarDays, format } from 'date-fns';

const useStyles = makeStyles((theme) => ({
    root: {
        width: '100%',
    },
    customDivider: {
        width: '100%',
        height: '1px',
        backgroundColor: '#e0e0e0',
        margin: theme.spacing(2, 0),
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
    },
    datePickerContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1)
    },
    cell: {
        border: '1px solid white',
    },
    verticallyCentered: {
        verticalAlign: 'middle',
    },
    evenRow: {
        backgroundColor: '#f2f2f2',
        '& td': {
            border: '1px solid #f2f2f2',
        },
    },
    tableHeader: {
        textAlign: 'left',
    }
}));

function PieceWork({ page_type }) {
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [scheduleData, setScheduleData] = useState({});
    const [rangeValue, setRangeValue] = useState([null, null]);
    const [selectedPlumbers, setSelectedPlumbers] = useState([]);
    const [plumberList, setPlumberList] = useState([]);
    const [myParams, setMyParams] = useState({});
    const [totalCost, setTotalCost] = useState(0);
    const [totalHours, setTotalHours] = useState(0);
    const [hourlyRate, setHourlyRate] = useState(null);
    const [insuranceRate, setInsuranceRate] = useState(null);
    const [glExpDate, setGlExpDate] = useState(null);
    const [wcExpDate, setWcExpDate] = useState(null);
    const [isApplyingDefaults, setIsApplyingDefaults] = useState(false);

    // New: trigger full remount of JSONTableEditor when bumped
    const [refreshKey, setRefreshKey] = useState(0);

    const navigate = useNavigate();
    const handleNavigation = (path) => {
        navigate(path);
    };

    useEffect(() => {
        CentralCalls.fetchTableData('employee', { inactive: 0 }).then(response => {
            const sortedResponse = response.slice().sort((a, b) => a.name.localeCompare(b.name));
            setPlumberList(sortedResponse);
        });
    }, []);

    const handleTableDataChange = (rows) => {
        const safeRows = Array.isArray(rows) ? rows : [];
        const totals = safeRows.reduce(
            (acc, row) => {
                const rowCost = Number(row?.cost);
                const rowHours = Number(row?.hours);
                acc.cost += Number.isFinite(rowCost) ? rowCost : 0;
                acc.hours += Number.isFinite(rowHours) ? rowHours : 0;
                return acc;
            },
            { cost: 0, hours: 0 }
        );

        setTotalCost(totals.cost);
        setTotalHours(totals.hours);
    };

    useEffect(() => {
        // Reset while new table data is loading for changed filters.
        setTotalCost(0);
        setTotalHours(0);
    }, [selectedPlumbers, startDate, endDate, page_type]);

    const handleStartDateChange = (newValue) => {
        setStartDate(newValue);
    };
    const handleEndDateChange = (newValue) => {
        setEndDate(newValue);
    };
    const handleSelectedPlumberChange = (newValue) => {
        setSelectedPlumbers(newValue);
        setHourlyRate(newValue[0]?.hourly_rate ? newValue[0].hourly_rate : null);
        setInsuranceRate(newValue[0]?.insurance_rate ? newValue[0].insurance_rate : null);
        setGlExpDate(newValue[0]?.gl_exp_date ? newValue[0].gl_exp_date : null);
        setWcExpDate(newValue[0]?.wc_exp_date ? newValue[0].wc_exp_date : null);
    }

    const handlePostSavePieceWorkData = async (row) => {
        // refresh totals and force grid remount so new values show
        console.log('PieceWork saved row:');
        console.log(row);
        try {
            // check for existing phase_status_cost row matching employee_oid and phase_status === row.oid
            const where = { employee_oid: selectedPlumbers[0].oid, phase_status_oid: row.oid };
            const existing = await CentralCalls.fetchTableData('phase_status_cost', where);
            if (existing && existing.length > 0) {
                // update existing row (include its oid)
                const existingOid = existing[0].oid;
                const dataToUpdate = { cost:row.cost, oid: existingOid };
                await CentralCalls.upsertData(dataToUpdate, 'phase_status_cost', 'update');
            } else {
                // insert new row
                await CentralCalls.upsertData({cost:row.cost, employee_oid: selectedPlumbers[0].oid, phase_status_oid: row.oid}, 'phase_status_cost', 'insert');
            }
        } catch (err) {
            console.error('Failed saving piece work row:', err);
        }
        // Totals are derived from table rows; JSONTableEditor data callback keeps them in sync.
    }
    const handlePostSaveScheduleData = (row) => {
        // Totals are derived from table rows; no separate totals fetch needed.
        setRefreshKey(k => k + 1);
    }
    const handleWipLinkClick = async (phaseRow) => {
        const path = '/wip?lot_oid=' + phaseRow.lot_oid;
        window.open(path, "_blank");
    }

    const applyDefaultHourlyCosts = async () => {
        if (!(selectedPlumbers?.length > 0 && startDate && endDate)) return;
        setIsApplyingDefaults(true);
        try {
            await CentralCalls.callStoredProcedureWithParams(
                'sp_peiceWorkDefaultHourlyCosts',
                {
                    employee_oids: selectedPlumbers.map(item => item.oid),
                    start_date: CentralCalls.sqlFormatdate(startDate),
                    end_date: CentralCalls.sqlFormatdate(endDate)
                }
            );
            // Bump refreshKey to refetch table rows, which also updates totals.
            setRefreshKey(k => k + 1);
            // Optionally: show a toast/snackbar here
        } catch (err) {
            console.error('Failed to apply default hourly costs', err);
            // Optionally: show a toast/snackbar here
        } finally {
            setIsApplyingDefaults(false);
        }
    };

    const sendToPrint = async () => {
        const qry = page_type === 'piece_work' ? '1' : '0';
        const urlParams =
            'is_piece_work=1' +
            '&employee_oids=' + encodeURIComponent(JSON.stringify(selectedPlumbers.map(item => item.oid))) +
            '&start_date=' + CentralCalls.sqlFormatdate(startDate) +
            '&end_date=' + CentralCalls.sqlFormatdate(endDate) +
            '&employee_name=' + selectedPlumbers[0].name +
            '&total_cost=' + totalCost;

        const path = '/ticketPrintOut?' + urlParams;
        window.open(path, "_blank");
    }

    const classes = useStyles();
    const customFunctions = [
        {
            display_name: 'WIP',
            type: 'overview',
            func: handleWipLinkClick
        }
    ]

    const procedureDetailsPieceWorkData = useMemo(() => ({
        procedureName: 'ui_sp_schedulePieceWork',
        parameters: {
            employee_oids: selectedPlumbers.map(item => item.oid),
            start_date: CentralCalls.sqlFormatdate(startDate),
            end_date: CentralCalls.sqlFormatdate(endDate)
        }
    }), [selectedPlumbers, startDate, endDate]);

    const procedureDetailsScheduleData = {
        procedureName: 'ui_sp_scheduleTable',
        parameters: { employee_oids: selectedPlumbers.map(item => item.oid), start_date: CentralCalls.sqlFormatdate(startDate), end_date: CentralCalls.sqlFormatdate(endDate) },
    };

    const insuranceCharge = (insuranceRate != null && totalCost)
        ? totalCost * Number(insuranceRate)
        : 0;
    const finalTotalCost = totalCost - insuranceCharge;

    const formatExpiringDate = (dateVal) => {
        if (!dateVal) return null;
        const dt = (typeof dateVal === 'string') ? new Date(dateVal) : dateVal;
        if (isNaN(dt)) return null;
        const urgent = differenceInCalendarDays(dt, new Date()) <= 31; 
        return { text: format(dt, 'MM/dd/yyyy'), urgent };
    };

    const formattedGl = formatExpiringDate(glExpDate);
    const formattedWc = formatExpiringDate(wcExpDate);

    const actionDisabled = !(selectedPlumbers && selectedPlumbers.length > 0 && startDate && endDate);

    return (
        <div className={classes.root}>
            {page_type === 'piece_work' ? (<>
                <h1>Piece Work</h1>
            </>
            ) : (<h1>Schedule</h1>)}

            <div className={classes.datePickerContainer}>
                <h4>Employee</h4>
                <Autocomplete
                    options={plumberList.sort((a, b) => a.name.localeCompare(b.name)) || []}
                    getOptionLabel={(option) => option.name}
                    multiple={true}
                    style={{ width: '350px' }}
                    onChange={(event, newValue) => {
                        handleSelectedPlumberChange(newValue);
                    }}
                    renderInput={(params) => (
                        <FormControl fullWidth required={true}>
                            <TextField {...params} variant="standard" required={true} />
                        </FormControl>
                    )}
                />

                <h4>Select Date Range</h4>
                <input type="date"
                    onChange={(e) => { handleStartDateChange(new Date(e.target.value)) }} />
                -
                <input type="date"
                    onChange={(e) => {
                        handleEndDateChange(new Date(e.target.value));
                    }} />

                {/* NEW: Apply Default Hourly Costs button */}
                <IconButton
                    disabled={actionDisabled || isApplyingDefaults}
                    color="primary"
                    aria-label="apply default hourly costs"
                    onClick={applyDefaultHourlyCosts}
                    size="medium"
                    title="Generate Hourly Costs"
                >
                    <FontAwesomeIcon icon={faDollarSign} />
                </IconButton>

                {/* Existing Print button */}
                <IconButton
                    disabled={actionDisabled}
                    color="primary"
                    aria-label="print"
                    onClick={() => { sendToPrint() }}
                    size="medium"
                    title="Print"
                >
                    <FontAwesomeIcon icon={faPrint} />
                </IconButton>
            </div>

            <div className={classes.customDivider}></div>

            {page_type === 'piece_work' ? (
                <>
                    <h4 style={{margin:'5px'}}>
                          {formattedGl && (
                            <>
                               <span style={{ color: formattedGl.urgent ? 'red' : 'inherit' }}>GL Exp: {formattedGl.text}</span>
                            </>
                        )}
                        {formattedWc && (
                            <>
                                {' '}· <span style={{ color: formattedWc.urgent ? 'red' : 'inherit' }}>WC Exp: {formattedWc.text}</span>
                            </>
                        )} 
                    </h4>
                      <h4 style={{ margin: '5px' }}>
                        Total Cost: {CentralCalls.formatTotal(totalCost)}
                        {insuranceRate != null && (
                            <>
                                {' '}· Insurance: {CentralCalls.formatTotal(insuranceCharge)} ({(Number(insuranceRate) <= 1 ? (Number(insuranceRate) * 100).toFixed(2) : Number(insuranceRate)).toString()}%)
                            </>
                        )}
                        {' '}· Total Payment: {CentralCalls.formatTotal(finalTotalCost)}
                    </h4>           
                    <h3 style={{margin:'5px'}}>Hours: {totalHours != null ? Number(totalHours).toFixed(2) : '0.00'} · ${hourlyRate} hr</h3>
                    <JSONTableEditor
                        key={refreshKey}
                        procedureDetails={procedureDetailsPieceWorkData}
                        onSave={handlePostSavePieceWorkData}
                        onDataChange={handleTableDataChange}
                        customFunctions={customFunctions}
                        sortOrder={{
                            'date_complete': 'asc'
                        }} ></JSONTableEditor>
                </>
            ) : (
                <>
                    <h3>Total Hours: {totalHours}</h3>
                    <JSONTableEditor
                        key={refreshKey}
                        procedureDetails={procedureDetailsScheduleData}
                        onSave={handlePostSaveScheduleData}
                        onDataChange={handleTableDataChange}
                        customFunctions={customFunctions}
                        sortOrder={{
                            'date_complete': 'asc'
                        }} ></JSONTableEditor>
                </>
            )}
        </div>
    );
}

export default PieceWork;
